import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProject,
  useProjectAssignments,
  useCreateAssignment,
  useUpdateAssignment,
  useConfirmAssignment,
  useDeleteAssignment,
  useUpdateProject,
} from '../../hooks/useProjects';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { ConfirmModal } from '../common/Modal';
import AssignmentForm from './AssignmentForm';
import type { ProjectAssignment, CreateAssignmentInput, UpdateAssignmentInput } from '../../services/api/projects';
import { deleteAssignment } from '../../services/api/projects';
import { useFlag } from '../../services/flagsContext';
import { formatDisplayDate, formatRate } from '../../utils/format';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

// MKS-293 — Phase 70's "Set billing currency" badge is gone. It flagged every
// row whose currency was 'CZK', on the theory that the DB default made CZK
// indistinguishable from "not confirmed yet". It is: an explicit pick of CZK
// stores exactly the same value, so the badge could never clear, and nearly
// every assignment is CZK. CZK is a normal, valid choice — nothing to warn about.

/** Render a billing rate with its currency — CZK keeps the existing
 * Kč-suffixed formatting; EUR/USD show the raw amount + currency code
 * since the persisted number is the foreign amount, not a CZK figure. */
function formatBillingRate(rate: number | null, currency: 'CZK' | 'EUR' | 'USD' | undefined): string {
  if (rate == null) return '-';
  if (!currency || currency === 'CZK') return formatRate(rate);
  return `${formatNumber(rate, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_EN_NUMBER_LOCALE)} ${currency}/hr`;
}

/**
 * MKS-358 — the allocation column. One assignment stores ONE figure, so the
 * cell renders the side that was stored rather than converting to a house unit:
 * converting needs the person's FTE and their working days, which this table
 * does not have and must not guess at.
 */
function formatAllocation(assignment: ProjectAssignment): string {
  if (assignment.allocationHours != null) {
    return `${formatNumber(assignment.allocationHours, { maximumFractionDigits: 1 }, LEGACY_EN_NUMBER_LOCALE)} h/mo`;
  }
  if (assignment.allocationShare != null) {
    return `${formatNumber(assignment.allocationShare * 100, { maximumFractionDigits: 1 }, LEGACY_EN_NUMBER_LOCALE)} % of FTE`;
  }
  return '-';
}

interface AssignmentManagerProps {
  projectId: number;
}

function formatPeriodDate(isoDate: string): string {
  // MKS-351 — dd.mm.yyyy from the shared util, which reads YYYY-MM-DD as a
  // calendar date and so cannot drift a day in a negative timezone.
  return formatDisplayDate(isoDate);
}

export default function AssignmentManager({ projectId }: AssignmentManagerProps) {
  const queryClient = useQueryClient();
  const { data: project } = useProject(projectId);
  const { data: assignments, isLoading } = useProjectAssignments(projectId);
  const createMutation = useCreateAssignment();
  const updateMutation = useUpdateAssignment();
  const confirmMutation = useConfirmAssignment();
  const deleteMutation = useDeleteAssignment();
  const updateProjectMutation = useUpdateProject();

  // MKS-358 — the allocation and status columns, and the one-click confirm.
  // With the flag off the table is the seven-column one it was.
  const allocationUi = useFlag('mks_358_assignment_allocation_ui');

  const [showForm, setShowForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<ProjectAssignment | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showPastAssignments, setShowPastAssignments] = useState(false);
  const [sort, setSort] = useState<{ key: 'user' | 'period'; dir: 'asc' | 'desc' }>({ key: 'user', dir: 'asc' });

  const toggleSort = (key: 'user' | 'period') => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  // Filter assignments: active = no endDate or endDate >= today
  const today = new Date().toISOString().slice(0, 10);
  const filteredAssignments = assignments
    ?.filter((a) => {
      if (showPastAssignments) return true;
      return !a.endDate || a.endDate >= today;
    })
    .sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (sort.key === 'user') {
        const nameA = a.user?.name ?? '';
        const nameB = b.user?.name ?? '';
        return nameA.localeCompare(nameB) * dir;
      }
      // period: sort by startDate
      const dateA = a.startDate ?? '';
      const dateB = b.startDate ?? '';
      return dateA.localeCompare(dateB) * dir;
    });

  // F22-003: Wrap mutateAsync calls in try/catch — previously a rejection
  // (409/network failure) bubbled up as an unhandledrejection that only Sentry
  // saw, while the modal closed silently. Pattern mirrors the F21-002 sweep in
  // UserDetailPage.tsx and LeadFormModal.tsx:237-246. React Query exposes
  // `mutation.error` for inline rendering if the caller wants it.
  const handleCreate = async (input: CreateAssignmentInput) => {
    try {
      await createMutation.mutateAsync({ projectId, input });
      setShowForm(false);
    } catch {
      // mutation error handled by React Query — leave the form open
    }
  };

  const handleUpdate = async (input: UpdateAssignmentInput) => {
    if (!editingAssignment) return;
    try {
      await updateMutation.mutateAsync({ projectId, assignmentId: editingAssignment.id, input });
      setEditingAssignment(null);
    } catch {
      // mutation error handled by React Query — leave the form open
    }
  };

  // MKS-358 — one click, one assignment: the confirm endpoint takes nothing but
  // the id, so there is no modal and nothing else about the row can move.
  const handleConfirm = async (assignmentId: number) => {
    try {
      await confirmMutation.mutateAsync({ projectId, assignmentId });
    } catch {
      // mutation error surfaced by the hook's error toast
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync({ projectId, assignmentId: deleteId });
      setDeleteId(null);
    } catch {
      // mutation error handled by React Query — leave the modal open
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!filteredAssignments) return;
    if (selectedIds.size === filteredAssignments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssignments.map((a) => a.id)));
    }
  };

  const handleBulkDelete = async () => {
    // Capture IDs as array before starting to avoid any race conditions
    const idsToDelete = Array.from(selectedIds);
    setIsBulkDeleting(true);
    try {
      // Use direct API calls to avoid triggering query invalidation after each delete
      await Promise.all(idsToDelete.map((id) => deleteAssignment(projectId, id)));
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
      // Invalidate assignments list and project stats
      queryClient.invalidateQueries({ queryKey: ['projectAssignments', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleToggleIsDefault = async (checked: boolean) => {
    await updateProjectMutation.mutateAsync({ id: projectId, input: { isDefault: checked } });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-24">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium text-gray-900">Team Assignments</h2>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showPastAssignments}
              onChange={(e) => setShowPastAssignments(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Show past assignments
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={project?.isDefault ?? false}
              onChange={(e) => handleToggleIsDefault(e.target.checked)}
              disabled={updateProjectMutation.isPending}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Assigned to everyone
          </label>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button size="sm" variant="danger" onClick={() => setShowBulkDeleteModal(true)}>
              Remove Selected ({selectedIds.size})
            </Button>
          )}
          <Button size="sm" onClick={() => { setShowForm(true); setEditingAssignment(null); }}>
            Add Assignment
          </Button>
        </div>
      </div>

      {/* Assignment Form - only for adding new */}
      {showForm && !editingAssignment && (
        <AssignmentForm
          projectId={projectId}
          assignment={null}
          onSubmit={async (input) => {
            await handleCreate(input as CreateAssignmentInput);
          }}
          onCancel={() => { setShowForm(false); }}
          isLoading={createMutation.isPending}
        />
      )}

      {/* Assignments Table */}
      {!filteredAssignments || filteredAssignments.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          {!assignments || assignments.length === 0
            ? 'No team members assigned yet.'
            : 'No active assignments. Check "Show past assignments" to see all.'}
        </p>
      ) : (
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Team assignments table">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 w-10">
                  <input
                    type="checkbox"
                    checked={filteredAssignments ? selectedIds.size === filteredAssignments.length && filteredAssignments.length > 0 : false}
                    onChange={toggleSelectAll}
                    aria-label="Select all assignments"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => toggleSort('user')}>
                    User {sort.key === 'user' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                  </button>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => toggleSort('period')}>
                    Period {sort.key === 'period' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                  </button>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hourly Costs</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Billing Rate</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Investment Rate</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                {allocationUi && (
                  <>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Allocation</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </>
                )}
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAssignments.map((assignment) => (
                editingAssignment?.id === assignment.id ? (
                  <tr key={assignment.id} className="bg-gray-50">
                    <td colSpan={allocationUi ? 10 : 8} className="px-4 py-3">
                      <AssignmentForm
                        projectId={projectId}
                        assignment={editingAssignment}
                        onSubmit={async (input) => {
                          await handleUpdate(input as UpdateAssignmentInput);
                        }}
                        onCancel={() => { setEditingAssignment(null); }}
                        isLoading={updateMutation.isPending}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={assignment.id} className={selectedIds.has(assignment.id) ? 'bg-primary-50' : ''}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(assignment.id)}
                        onChange={() => toggleSelect(assignment.id)}
                        aria-label={`Select assignment for ${assignment.user?.name ?? `user #${assignment.userId}`}`}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {assignment.user?.name ?? `User #${assignment.userId}`}
                      {assignment.user?.email && (
                        <span className="text-gray-500 ml-1 text-xs">({assignment.user.email})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {assignment.startDate ? formatPeriodDate(assignment.startDate) : 'Start'} - {assignment.endDate ? formatPeriodDate(assignment.endDate) : 'Ongoing'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {assignment.hourlyCosts != null ? (
                        formatRate(assignment.hourlyCosts)
                      ) : assignment.user?.defaultHourlyCosts != null ? (
                        <span className="text-gray-400" title="Default rate from user profile">
                          {formatRate(assignment.user.defaultHourlyCosts)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatBillingRate(assignment.billingRate, assignment.billingRateCurrency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatRate(assignment.userInvestment)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {assignment.roleName || '-'}
                    </td>
                    {allocationUi && (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatAllocation(assignment)}
                          {assignment.allocations && assignment.allocations.length > 0 && (
                            <span
                              className="block text-xs text-gray-400"
                              title="Some months override the allocation above."
                            >
                              {assignment.allocations.length} month
                              {assignment.allocations.length > 1 ? 's' : ''} overridden
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {assignment.status === 'tentative' ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              Tentative
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">Confirmed</span>
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right space-x-2">
                      {allocationUi && assignment.status === 'tentative' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleConfirm(assignment.id)}
                          disabled={confirmMutation.isPending}
                          className="text-green-700"
                        >
                          Confirm
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => { setEditingAssignment(assignment); setShowForm(false); }}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(assignment.id)} className="text-red-600">
                        Remove
                      </Button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Remove Assignment"
        message="Are you sure you want to remove this team member from the project?"
        confirmText="Remove"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Remove Selected Assignments"
        message={`Are you sure you want to remove ${selectedIds.size} team member${selectedIds.size > 1 ? 's' : ''} from the project?`}
        confirmText={`Remove ${selectedIds.size}`}
        variant="danger"
        isLoading={isBulkDeleting}
      />
    </div>
  );
}
